import { Button } from '@fluentui/react-components';
import { ExternalLink, ShieldCheck } from 'lucide-react';

import type { TenantDetail } from '../../api/tenantRegistry';
import { useOptionalEnvironment } from '../../context/EnvironmentContext';
import { grantUrlWithState } from '../../lib/tenantConnectRedirect';

// R-FD-055 / FD-566 — "Grant permissions", one component for every place it appears.
//
// PREFERS `grantUrl`: /authorize + .default + prompt=consent, built by the Manager for
// an application created with the console reply URL. Same tab, and Microsoft returns
// the admin to /tenants/consented, which re-checks. NOT /adminconsent: on a tenant that
// requires device authentication that endpoint fails AADSTS50097 and records nothing
// (FD-559).
//
// FALLS BACK to `consentUrl` in a NEW tab for an application created before R-FD-055,
// which has no console reply URL — pinning one there would fail after the admin had
// already acted (the FD-547 class). That path is today's behaviour, unchanged.
export default function GrantPermissionsButton({
  tenant,
  label = 'Grant permissions',
}: {
  tenant: Pick<TenantDetail, 'tenantId' | 'grantUrl' | 'consentUrl'>;
  label?: string;
}) {
  // Optional: an environment-less render (a unit test) still works; the stash then
  // records '' and the return keeps whatever environment is active.
  const environmentId = useOptionalEnvironment()?.environmentId ?? '';

  if (tenant.grantUrl) {
    return (
      <Button
        appearance="primary"
        icon={<ShieldCheck size={16} strokeWidth={1.5} />}
        as="a"
        href={grantUrlWithState(tenant.grantUrl, { tenantId: tenant.tenantId, environmentId })}
      >
        {label}
      </Button>
    );
  }
  if (tenant.consentUrl) {
    return (
      <Button
        appearance="primary"
        icon={<ExternalLink size={16} strokeWidth={1.5} />}
        as="a"
        href={tenant.consentUrl}
        target="_blank"
        rel="noreferrer"
      >
        Trigger admin consent
      </Button>
    );
  }
  return null;
}
